import { FC, useMemo } from 'react';
import { Grid, Typography, Box, CircularProgress, Alert, Button } from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianDashboardHook } from '../../hooks/useGuardianDashboardHook';
import { useGuardianEventsHook, useGuardianMessagesHook } from '../../hooks/useGuardianEventsCommunicationHook';
import { SummaryCards, SummaryCardData, UpcomingItems } from '../../../dashboard/components/shared';
import CalendarComponent from '../../../../components/calendarComponent/calendarComponent';
import {
  School,
  Receipt,
  TrendingUp,
  Payment,
  Description,
  Event,
} from '@mui/icons-material';
import { pages } from '../../../../../utils/common';
import { EventInput } from '@fullcalendar/core';
import { useNavigate } from 'react-router-dom';

const GuardianDashboardView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId, children } = useGuardianState();
  const navigate = useNavigate();
  const selectedChild = children.find((child) => child.id === selectedChildId);

  const { data: dashboardData, isLoading: isDashboardLoading, error: dashboardError } =
    useGuardianDashboardHook(selectedChildId || '', { page: 1, limit: 10 });

  const { data: eventsData, isLoading: isEventsLoading } = useGuardianEventsHook(
    selectedChildId || '',
    { page: 1, limit: 5, status: 'upcoming' }
  );

  const { data: messagesData, isLoading: isMessagesLoading } = useGuardianMessagesHook(
    selectedChildId || '',
    { page: 1, limit: 5, read: false }
  );

  const summaryCards = useMemo<SummaryCardData[]>(() => {
    if (!dashboardData) return [];

    return [
      {
        id: 'attendance',
        title: translateText('guardian.dashboard.attendanceSummary.title'),
        value: `${dashboardData.summary.attendance.percentage}%`,
        subtitle: `${translateText('guardian.dashboard.attendanceSummary.present')}: ${dashboardData.summary.attendance.present} | ${translateText('guardian.dashboard.attendanceSummary.absent')}: ${dashboardData.summary.attendance.absent}`,
        icon: <School />,
        color: 'success',
        navigationPath: `${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${pages.guardian.subPages?.academics?.subPages?.attendance?.path}`,
        isCritical: true,
      },
      {
        id: 'academics',
        title: translateText('guardian.dashboard.academicPerformance.title'),
        value: dashboardData.summary.academics.averageScore.toString(),
        subtitle: `${translateText('guardian.dashboard.academicPerformance.recentAssessments')}: ${dashboardData.summary.academics.recentAssessments}`,
        icon: <TrendingUp />,
        color: 'primary',
        navigationPath: `${pages.guardian.path}${pages.guardian.subPages?.academics?.path}`,
        isCritical: true,
      },
      {
        id: 'fees',
        title: translateText('guardian.dashboard.feeStatus.title'),
        value: `₹${dashboardData.summary.fees.pending.toLocaleString()}`,
        subtitle: `${translateText('guardian.dashboard.feeStatus.paid')}: ₹${dashboardData.summary.fees.paid.toLocaleString()}`,
        icon: <Receipt />,
        color: dashboardData.summary.fees.pending > 0 ? 'warning' : 'success',
        navigationPath: `${pages.guardian.path}${pages.guardian.subPages?.fees?.path}`,
        isCritical: false,
      },
    ];
  }, [dashboardData, translateText]);

  const upcomingEvents = useMemo(() => {
    if (!eventsData?.data) return [];
    return eventsData.data.map((event) => ({
      id: event.id,
      title: event.title,
      description: event.description,
      date: event.date,
      type: event.type,
      navigationPath: `${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}`,
    }));
  }, [eventsData]);

  const recentAnnouncements = useMemo(() => {
    if (!messagesData?.data) return [];
    return messagesData.data.map((message) => ({
      id: message.id,
      title: message.subject,
      description: message.message,
      date: message.date,
      type: 'announcement',
      navigationPath: `${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}`,
    }));
  }, [messagesData]);

  const getEventTypeColor = (type: string): string => {
    switch (type) {
      case 'school':
        return '#1976d2';
      case 'class':
        return '#388e3c';
      case 'sports':
        return '#f57c00';
      case 'cultural':
        return '#7b1fa2';
      default:
        return '#616161';
    }
  };

  const calendarEvents = useMemo<EventInput[]>(() => {
    if (!eventsData?.data) return [];
    
    return eventsData.data.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.date + (event.time ? `T${event.time}` : ''),
      backgroundColor: getEventTypeColor(event.type),
      borderColor: getEventTypeColor(event.type),
      extendedProps: {
        ...event,
      },
    }));
  }, [eventsData]);

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-dashboard-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isDashboardLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-dashboard-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('guardian.dashboard.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (dashboardError) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-dashboard-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('guardian.dashboard.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={3}
      sx={{ p: 3 }}
      data-testid="guardian-dashboard-container"
    >
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {translateText('guardian.dashboard.title')}
            </Typography>
            {selectedChild && (
              <Typography variant="body2" color="text.secondary">
                {translateText('guardian.dashboard.selectedChild')}: {selectedChild.name}
                {selectedChild.grade && ` - ${selectedChild.grade}${selectedChild.section ? ` ${selectedChild.section}` : ''}`}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Payment />}
              onClick={() => navigate(`${pages.guardian.path}${pages.guardian.subPages?.fees?.path}`)}
              data-testid="guardian-dashboard-quick-action-fees"
            >
              {translateText('guardian.dashboard.quickActions.payFees')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Description />}
              onClick={() => navigate(`${pages.guardian.path}${pages.guardian.subPages?.document?.path}`)}
              data-testid="guardian-dashboard-quick-action-documents"
            >
              {translateText('guardian.dashboard.quickActions.viewDocuments')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Event />}
              onClick={() => navigate(`${pages.guardian.path}${pages.guardian.subPages?.['events-communication']?.path}`)}
              data-testid="guardian-dashboard-quick-action-events"
            >
              {translateText('guardian.dashboard.quickActions.viewEvents')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<School />}
              onClick={() => navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}`)}
              data-testid="guardian-dashboard-quick-action-academics"
            >
              {translateText('guardian.dashboard.quickActions.viewAcademics')}
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <SummaryCards
          cards={summaryCards}
          columns={{ xs: 12, sm: 6, md: 4 }}
          dataTestId="guardian-dashboard-summary-cards"
          maxMobileCards={3}
        />
      </Grid>

      <Grid item xs={12} md={8}>
        <CalendarComponent
          events={calendarEvents}
          data-testid="guardian-dashboard-calendar"
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <UpcomingItems
          title={translateText('guardian.dashboard.upcomingEvents.title')}
          items={upcomingEvents}
          isLoading={isEventsLoading}
          emptyMessage={translateText('guardian.dashboard.upcomingEvents.noEvents')}
          dataTestId="guardian-dashboard-upcoming-events"
          maxItems={5}
        />
        <UpcomingItems
          title={translateText('guardian.dashboard.recentAnnouncements.title')}
          items={recentAnnouncements}
          isLoading={isMessagesLoading}
          emptyMessage={translateText('guardian.dashboard.recentAnnouncements.noAnnouncements')}
          dataTestId="guardian-dashboard-recent-announcements"
          maxItems={5}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianDashboardView;
